import { useEffect, useState } from "react";

import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { getAllDoctorsAssistant } from "../../Api/DoctorAssistant/DoctorAssistant.Api";
import { exportReportFile } from "../../Api/ReportDetails/Report.Api";
import { getAllDoctorsAndPromoCodeTypes, getAllActiveProviders } from "../../Api/Signin/Transaction.Api";
import { ReactComponent as ExcelIcon } from "../../Assets/Images/Icon/ExcelWhiteIcon.svg";
import { ReactComponent as PdfIcon } from "../../Assets/Images/Icon/PDFWhiteIcon.svg";
import CalenderStar from "../../Components/atoms/Calender/CalenderType1/CalenderBackdate";
import CalenderFutureDate from "../../Components/atoms/Calender/CalenderType1/CalenderInfuture.js";
import MultiSelectCheckList from "../../Components/atoms/MultiSelect/MultiSelectCheckList";
import { MultipleSelectOption } from "../../Components/atoms/MultiSelect/MultipleSelectOption";
import { Heading2 } from "../../Components/atoms/Typography/Heading/Heading";
import { ViewDoctorDetails } from "../../Components/organisms/ViewDoctorDetails/ViewDoctorDetails";
import { REPORTS } from "../../Constants/ReportTypes";
import { TransactionFields } from "../../Constants/TransactionSelectFields";
import { useAuth } from "../../Store/AuthContext";
import {
  downloadReportExport,
  getReportExportErrorMessage,
  normalizeExportFileType,
} from "../../Utils/FileDownload/FileDownload";
import { getProviderDisplayName } from "../../Utils/WordFormatter/ProviderAlias";
import useApiData from "../../Utils/Hooks/useApiData";
import useOverlayState from "../../Utils/Hooks/useOverlayState";

dayjs.extend(customParseFormat);

const URGENT_REPORT_OPTIONS = [
  { label: "AMR - Appointments Report", value: "Appointments" },
  { label: "AMR - Availability Report", value: "Availability" },
  {
    label: "AMR - Appointment Status Report",
    value: "AppointmentStatus",
  },
];

export const ReportFilters = () => {
  const location = useLocation();
  const { reportNo, reportsName } = location.state || {};
  let reportObject;
  let reportNumber = reportNo;
  let reportName = reportsName;

  if (reportName === undefined && reportNumber === undefined) {
    const pathParts = window.location.pathname.split("/");
    const urlreportname = pathParts[pathParts.length - 1];
    reportObject = REPORTS.filter((report) => report.urlName === urlreportname);
    reportName = reportObject[0].name;
    reportNumber = reportObject[0].id;
  } else {
    reportObject = REPORTS.filter((report) => report.name === reportName);
  }
  const [startDate, setStartDate] = useState("01/01/2025");
  const [endDate, setEndDate] = useState(null);
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const { state } = useAuth();
  const [activeExportType, setActiveExportType] = useState(null);
  const { t } = useTranslation();
  const { OverlayState, closeOverlay } = useOverlayState();
  const [doctors, setDoctors] = useState([]);
  const [isDoctorsLoading, setIsDoctorsLoading] = useState(false);
  const searchText = "";
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [subReportType, setSubReportType] = useState("");
  const PROVIDER_ALL = "all";
  const [providerList, setProviderList] = useState([
    { label: "Select All", key: PROVIDER_ALL, id: null },
  ]);
  const [selectedProvider, setSelectedProvider] = useState("");

  const [pageData, setPageData] = useState({
    currentPage: 1,
    pageCount: 0,
    pageSize: 10,
    totalPages: 0,
    totalRecordsCount: 0,
  });

  const [validationErrors, setValidationErrors] = useState({
    doctors: false,
    columns: false,
    provider: false,
  });

  const { control, getValues, watch } = useForm({
    defaultValues: {
      promoCode: "",
      endDate: null,
      doctorIds: [],
      dateRange: [],
      myOptions: [],
      reusability: false,
    },
    criteriaMode: "all",
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const selectedDoctors = watch("doctorIds");

  const apiCall = async () => {
    const langCode = String(state?.lang?.code || "");
    const response = await getAllDoctorsAssistant({
      page: pageData.currentPage,
      size: pageData.pageSize,
      searchText: searchText,
      language: langCode.toUpperCase(),
    });

    if (response.type === 0) {
      return response;
    } else {
      console.error("Failed to fetch doctor data");
    }
  };

  const subReportName =
    URGENT_REPORT_OPTIONS.find((option) => option.value === subReportType)
      ?.label || "";

  const { refetch } = useApiData({
    apiCall: apiCall,
    dependency: [pageData.currentPage, pageData.pageSize, searchText],
    pagination: {
      isServerSidePagination: true,
      setApiData: (totalRecordsCount, pageSize, pageNumber, pagesCount) => {
        if (
          pageData.currentPage !== pageNumber ||
          pageSize !== pageData.pageSize
        ) {
          setPageData({
            ...pageData,
            currentPage:
              pageData.currentPage !== pageNumber
                ? pageNumber
                : pageData.currentPage,
            pageSize:
              pageSize !== pageData.pageSize ? pageSize : pageData.pageSize,
            totalPages: pagesCount,
            totalRecordsCount: totalRecordsCount,
          });
        } else {
          setPageData({
            ...pageData,
            totalPages: pagesCount,
            totalRecordsCount: totalRecordsCount,
          });
        }
      },
    },
  });

  const getTransactionColumns = async () => {
    const allCols = TransactionFields.map((field) => ({
      id: field.key,
      name: field.name,
    }));

    if (selectedProvider === PROVIDER_ALL) {
      return allCols;
    } else if (selectedProvider === "first doctor" || selectedProvider === "firstdoctor") {
      return allCols.filter(c => !["healthcareProvider", "doctorFee", "hospitalFee"].includes(c.id));
    } else {
      const allowed = ["txId", "txDate", "stripeStatus", "appointmentStatus", "patientId", "patientName", "doctorId", "doctorName", "repeatCodeType", "repeatCode", "discount", "doctorFee", "hospitalFee", "convenienceFee", "vat", "totalCollected", "paymentGatewayFee", "modeOfConsultation", "paymentMode", "locationName"];
      return allCols.filter(c => allowed.includes(c.id));
    }
  };

  const getExportErrorMessage = (message) => {
    return getReportExportErrorMessage(message, {
      noRecordsMessage: t("validation:report_no_records"),
      serverErrorMessage: t("validation:server-error"),
    });
  };

  const handleReportDownload = async (reportNumber, reportName, fileType) => {
    setActiveExportType(`${reportNumber}:${fileType}`);
    setError(null);

    try {
      const doctorIds = getValues("doctorIds") || [];
      const doctorIdsString = doctorIds.map((d) => d.id).join(",");
      
      const isFirstDoctorSelected = 
        reportName !== "Transactions" ||
        selectedProvider === "first doctor" ||
        selectedProvider === "firstdoctor";

      const finalDoctorsString = isFirstDoctorSelected ? doctorIdsString : "ALL";

      setValidationErrors({ doctors: false, columns: false, provider: false });

      let finalReportName;

      let formattedStartDate =
        startDate !== null
          ? dayjs(startDate, ["DD/MM/YYYY", "YYYY-MM-DD"]).format("YYYY-MM-DD")
          : null;
      let formattedEndDate =
        endDate !== null
          ? dayjs(endDate, ["DD/MM/YYYY", "YYYY-MM-DD"]).format("YYYY-MM-DD")
          : null;

      // Handle Invalid Date results
      formattedStartDate =
        formattedStartDate === "Invalid Date" ? null : formattedStartDate;
      formattedEndDate =
        formattedEndDate === "Invalid Date" ? null : formattedEndDate;

      if (reportName === "Appointment Monitoring") {
        // Validate Dropdown Selection
        if (!subReportType) {
          setError("Please select a report type.");
          return;
        }
        // Swap report name
        finalReportName = subReportType;
      } else {
        finalReportName = reportName;
      }

      const doctorsInvalid =
        reportObject[0].filterDoctor &&
        (reportName !== "Transactions" ||
          selectedProvider === "first doctor" ||
          selectedProvider === "firstdoctor") &&
        !doctorIdsString;
      const backendFileType = normalizeExportFileType(fileType);

      if (reportName === "Transactions") {
        const columnsInvalid = selectedColumns.length === 0;
        const providerInvalid = !selectedProvider;

        setValidationErrors({
          doctors: doctorsInvalid,
          columns: columnsInvalid,
          provider: providerInvalid,
        });

        if (doctorsInvalid || columnsInvalid || providerInvalid) {
          return;
        }

        const providerObj = providerList.find((p) => p.key === selectedProvider);
        const providerId = providerObj ? providerObj.id : null;

        const response = await exportReportFile({
          reportName: "TransactionsReport",
          fileType: backendFileType,
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          doctors: finalDoctorsString || "ALL",
          selectedColumns,
          providerId,
        });

        if (response?.type === 0 && response?.data) {
          downloadReportExport(response, {
            reportName,
            fileType: backendFileType,
          });
          setError(null);
          return;
        }

        setError(getExportErrorMessage(response?.message));
        return;
      }

      if (doctorsInvalid) {
        setValidationErrors({ doctors: true, columns: false });
        return;
      }

      const response = await exportReportFile({
        reportName: finalReportName,
        fileType: backendFileType,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        doctors: finalDoctorsString || "ALL",
      });

      if (response?.type === 0 && response?.data) {
        downloadReportExport(response, {
          reportName:
            reportName === "Appointment Monitoring" && subReportName
              ? subReportName
              : reportName,
          fileType: backendFileType,
        });
        setError(null);
        return;
      }

      setError(getExportErrorMessage(response?.message));
    } catch (err) {
      setError(getExportErrorMessage(err?.message));
    } finally {
      setActiveExportType(null);
    }
  };

  const GetDoctors = async () => {
    try {
      setIsDoctorsLoading(true);
      const response = await getAllDoctorsAndPromoCodeTypes();
      if (response.type === 0) {
        const doctorsArray = Array.isArray(response?.data?.doctors)
          ? response.data.doctors
          : [];

        const standardizedDoctors = doctorsArray
          .map((doctor) => ({
            ...doctor,
            id: doctor.id.toLowerCase(),
          }))
          .sort((a, b) => {
            const nameA = (a.name || "").toLowerCase();
            const nameB = (b.name || "").toLowerCase();
            return nameA.localeCompare(nameB);
          });
        setDoctors(standardizedDoctors);
      }
    } catch (exception) {
      console.error("Error fetching doctors:", exception);
    } finally {
      setIsDoctorsLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await getAllActiveProviders();
      if (response?.type === 0 && Array.isArray(response.data)) {
        const sortedProviders = response.data
          .map((p) => ({
            label: getProviderDisplayName(p.providerName),
            key: p.providerName.toLowerCase(),
            id: p.id,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        setProviderList([
          { label: "Select All", key: PROVIDER_ALL, id: null },
          ...sortedProviders,
        ]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await GetDoctors();
      if (reportName === "Transactions") {
        await fetchProviders();
      }
    };

    fetchData();
  }, [reportName]);

  return (
    <>
      <Grid container direction={"column"}>
        <Grid container direction={"column"}>
          <Grid
            item
            sx={{
              width: "fit-content",
              maxWidth: "100%",
              marginBottom: "20px",
              borderRadius: "5px",
              padding: "8px",
            }}
          >
            <Paper
              sx={{
                padding: "5px 17px",
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow: "0px 1px 3px rgba(0,0,0,0.15)",
              }}
            >
              <Typography
                sx={{
                  fontFamily: `"SF Pro Text", sans-serif`,
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  display: "inline",
                }}
              >
                <span
                  style={{ color: "#0062CC", cursor: "pointer" }}
                  onClick={() => navigate("/reports")}
                >
                  Reports
                </span>
                <span
                  style={{ color: "#717171" }}
                >{` > ${reportName} Report`}</span>
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Grid
          item
          sx={{
            width: "50%",
            marginLeft: "10px",
            marginBottom: "20px",
          }}
        >
          <Heading2 text={`${reportName} Report`} />
        </Grid>

        <Paper
          sx={{
            padding: "10px 10px",
            boxShadow: "0px 0px 25px 0px rgba(0, 0, 0, 0.05)",
            borderRadius: "10px",
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
            position: "relative",

            // Responsive
            "@media (max-width: 1200px)": { maxWidth: "95%" },
            "@media (max-width: 900px)": { maxWidth: "100%" },
            "@media (max-width: 600px)": { maxWidth: "100%" },
          }}
        >
          <Grid item>
            <Grid
              container
              direction={"column"}
              spacing={1}
              sx={{
                padding: "20px 15px",
                marginBottom: "5px",
              }}
            >
              {/* Appointment Monitoring now uses separate date picker */}
              <Grid item marginLeft={"2px"} sx={{ width: "100%" }}>
                <Stack spacing={2} direction="row" sx={{ padding: "8px 1px" }}>
                  {reportName === "Appointment Monitoring" ? (
                    <CalenderFutureDate
                      labelStart="Start date"
                      labelEnd="End date"
                      onChange={(start, end) => {
                        setStartDate(start);
                        setEndDate(end);
                      }}
                    />
                  ) : (
                    reportObject[0]?.filterDate && (
                      <CalenderStar
                        labelStart="Start date"
                        labelEnd="End date"
                        onChange={(start, end) => {
                          setStartDate(start);
                          setEndDate(end);
                        }}
                      />
                    )
                  )}
                </Stack>
              </Grid>

              {reportName === "Appointment Monitoring" && (
                <Grid item marginLeft={"2px"} sx={{ width: "100%" }}>
                  {/* Report Type Dropdown */}
                  <Stack
                    spacing={2}
                    direction="row"
                    sx={{ padding: "8px 1px", marginTop: "10px" }}
                  >
                    <TextField
                      select
                      required
                      label="Select report type"
                      value={subReportType}
                      onChange={(e) => setSubReportType(e.target.value)}
                      size="small"
                      sx={{ width: "100%" }}
                      InputLabelProps={{
                        sx: {
                          fontSize: "15px",
                          transform: "translate(14px, 12px) scale(1)",
                          "&.MuiInputLabel-shrink": {
                            fontSize: "10px",
                            transform: "translate(14px, -6px) scale(1)",
                          },
                          "& .MuiFormLabel-asterisk": {
                            color: "#d32f2f",
                          },
                        },
                      }}
                    >
                      {URGENT_REPORT_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Grid>
              )}

              {reportObject[0].name === "Transactions" && (
                <Grid item marginLeft={"2px"} sx={{ width: "100%" }}>
                  <Stack
                    spacing={2}
                    direction="row"
                    sx={{ padding: "8px 1px", marginTop: "10px" }}
                  >
                    <TextField
                      select
                      required
                      label="Select healthcare provider"
                      value={selectedProvider}
                      error={validationErrors.provider}
                      helperText={validationErrors.provider ? "This field is required." : ""}
                      onChange={(e) => {
                        setSelectedProvider(e.target.value);
                        setSelectedColumns([]);
                      }}
                      size="small"
                      sx={{ width: "100%" }}
                      InputLabelProps={{
                        sx: {
                          fontSize: "15px",
                          transform: "translate(14px, 12px) scale(1)",
                          "&.MuiInputLabel-shrink": {
                            fontSize: "10px",
                            transform: "translate(14px, -6px) scale(1)",
                          },
                          "& .MuiFormLabel-asterisk": {
                            color: "#d32f2f",
                          },
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select healthcare provider</em>
                      </MenuItem>
                      {providerList.map((option) => (
                        <MenuItem key={option.key} value={option.key}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Grid>
              )}

              {reportObject[0].filterDoctor && (reportName !== "Transactions" || selectedProvider === "first doctor" || selectedProvider === "firstdoctor") && (
                <Grid>
                  <Stack
                    sx={{
                      padding: "12px 12px",
                      marginBottom: "15px",
                    }}
                  >
                    {OverlayState?.record && (
                      <ViewDoctorDetails
                        data={OverlayState.record}
                        close={closeOverlay}
                        OpenDrawer={OverlayState.visible}
                        loadDoctors={refetch}
                      />
                    )}

                    <Stack
                      direction={"row"}
                      justifyContent={"flex-start"}
                      width={"100%"}
                      spacing={2}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          maxWidth: "100%",
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        <MultipleSelectOption
                          control={control}
                          name="doctorIds"
                          label={t("glossary:report.select_doctor")}
                          placeholder={t(
                            "glossary:finance-add-promo-code.placeholder.select-doctors",
                          )}
                          options={doctors}
                          loading={isDoctorsLoading}
                          menuItemKey="id"
                          menuItemLabel="name"
                          limitTags={2}
                          required={true}
                          value={selectedDoctors || []}
                          getOptionLabel={(option) => option?.name || ""}
                          error={validationErrors.doctors}
                          helperText={
                            validationErrors.doctors
                              ? "This field cannot be empty."
                              : ""
                          }
                          sx={{
                            width: "100%",
                            "& .MuiInputBase-root": {
                              width: "100%",
                              maxWidth: "100%",
                            },
                            "& .MuiAutocomplete-tag": {
                              maxWidth: "calc(50% - 16px)",
                              margin: "2px 4px !important",
                            },
                            "& .MuiChip-label": {
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "100%",
                            },
                            "& .MuiAutocomplete-input": {
                              minWidth: "30px !important",
                              width: "auto !important",
                            },
                          }}
                        />
                      </Box>
                    </Stack>
                  </Stack>
                </Grid>
              )}

              {reportObject[0].name === "Transactions" && (
                <Grid container spacing={1}>
                  <Stack
                    sx={{
                      padding: "12px 12px",
                      marginBottom: "10px",
                      marginLeft: "10px",
                    }}
                  >
                    <Box
                      sx={{
                        p: 4,
                        maxWidth: "100%",
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        padding: "20px 24px",
                      }}
                    >
                      <Typography variant="h8" sx={{ mb: 1 }}>
                        Column(s) <span style={{ color: "#d32f2f" }}>*</span>
                      </Typography>

                      <MultiSelectCheckList
                        key={selectedProvider}
                        apiCall={getTransactionColumns}
                        onChangeSelected={(values) =>
                          setSelectedColumns(values)
                        }
                        helperText={
                          validationErrors.columns &&
                          selectedColumns.length === 0
                            ? t("validation:report_required_column")
                            : ""
                        }
                      />
                    </Box>
                  </Stack>
                </Grid>
              )}
            </Grid>

            <Grid item marginLeft={"26px"}>
              <Stack
                direction={"row"}
                justifyContent={"flex-end"}
                width={"100%"}
                spacing={2}
                sx={{
                  padding: "12px 22px",
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={() =>
                    handleReportDownload(reportNumber, reportName, "excel")
                  }
                  disabled={Boolean(activeExportType)}
                  sx={{
                    width: 185,
                    borderRadius: "10px",
                    textTransform: "none",
                    borderColor: "#0062CC",
                    backgroundColor: "#0062CC",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    "&:hover": {
                      backgroundColor: "#115fb8",
                      borderColor: "#115fb8",
                    },
                  }}
                >
                  {activeExportType === `${reportNumber}:excel` ? (
                    <CircularProgress
                      size={22}
                      thickness={5}
                      sx={{ color: "#fff" }}
                    />
                  ) : (
                    <ExcelIcon width={"17px"} height={"18px"} />
                  )}
                  Generate Excel
                </Button>

                <Button
                  color="secondary"
                  size="large"
                  onClick={() =>
                    handleReportDownload(reportNumber, reportName, "pdf")
                  }
                  disabled={Boolean(activeExportType)}
                  sx={{
                    width: 180,
                    borderRadius: "10px",
                    textTransform: "none",
                    borderColor: "#0062CC",
                    backgroundColor: "#0062CC",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    "&:hover": {
                      backgroundColor: "#115fb8",
                      borderColor: "#115fb8",
                    },
                  }}
                >
                  {activeExportType === `${reportNumber}:pdf` ? (
                    <CircularProgress
                      size={22}
                      thickness={5}
                      sx={{ color: "#fff" }}
                    />
                  ) : (
                    <PdfIcon width={"18px"} height={"18px"} />
                  )}
                  Generate PDF
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {error && (
            <Grid item>
              <div style={{ color: "red", textAlign: "center" }}>{error}</div>
            </Grid>
          )}
        </Paper>
      </Grid>
    </>
  );
};
